/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Enhanced Error Logging for AppDynamics Mobile
 * Provides detailed error context and stack traces
 */

import { Platform } from "react-native";
import store from "../store";
import { logger } from "./logger";
import {
  reportError as baseReportError,
  Platform as InstrumentationPlatform,
} from "./Instrumentation";
import {getLocalTimestamp}  from "../api/apiClient";
// Enhanced error interface with detailed context
export interface EnhancedErrorContext {
  message: string;
  severity: "ERROR" | "WARN" | "INFO";
  stack?: string;
  component?: string;
  screen?: string;
  action?: string;
  userId?: string;
  userName?: string;
  locationId?: string;
  locationName?: string;
  apiEndpoint?: string;
  httpMethod?: string;
  statusCode?: number;
  responseTime?: number;
  platform?: string;
  appVersion?: string;
  timestamp?: string;
  errorId?: string;
  capturedByInterceptor?: boolean | string;
  componentStack?: string;
  props?: string;
  [key: string]: any;
}

/**
 * Enhanced error class that captures detailed context
 */
export class DetailedError extends Error {
  public readonly context: EnhancedErrorContext;
  public readonly timestamp: string;
  public readonly errorId: string;

  constructor(message: string, context: Partial<EnhancedErrorContext> = {}) {
    super(message);
    this.name = "DetailedError";
    this.timestamp = getLocalTimestamp()
    this.errorId = this.generateErrorId();

    // Ensure we capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, DetailedError);
    }

    // If no stack was provided in context and we don't have one, try to generate it
    const stackTrace = context.stack || this.stack || new Error().stack;

    this.context = {
      message,
      severity: "ERROR",
      stack: stackTrace,
      platform: Platform.OS,
      timestamp: this.timestamp,
      errorId: this.errorId,
      ...this.captureUserContext(),
      ...context,
    };
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private captureUserContext() {
    try {
      const state = store.getState();
      const user = state?.auth?.user;
      const location = state?.appData?.selectedLocation;

      return {
        userId: user?.email || user?.mail || user?.preferred_username,
        userName:
          user?.name ||
          user?.displayName ||
          user?.given_name ||
          user?.preferred_username,
        locationId: location?.id || location?.locationId,
        locationName: location?.name || location?.locationName,
        appVersion: process.env.REACT_APP_VERSION || "1.0.0",
      };
    } catch (e) {
      return {};
    }
  }
}

/**
 * Enhanced error logging function with detailed context capture
 */
export const logDetailedError = (
  error: Error | string,
  context: Partial<EnhancedErrorContext> = {}
) => {
  let detailedError: DetailedError;

  if (error instanceof DetailedError) {
    detailedError = error;
  } else if (error instanceof Error) {
    detailedError = new DetailedError(error.message, {
      ...context,
      stack: error.stack,
    });
  } else {
    detailedError = new DetailedError(String(error), context);
  }

  // Enhanced console logging for development
  if (process.env.NODE_ENV === 'development') {
    logger.group(`🚨 Enhanced Error [${detailedError.errorId}]`);
    logger.error("Message:", detailedError.message);
    logger.error("Context:", detailedError.context);
    logger.error("Stack:", detailedError.stack);
    logger.groupEnd();
  }

  // Send to AppDynamics
  if (InstrumentationPlatform.isMobile) {
    reportToAppDynamicsMobile(detailedError);
  } else {
    // Enhanced web reporting with detailed context
    const enrichedMessage = createEnrichedErrorMessage(detailedError);
    const exceptionContextMessage = createExceptionContextMessage(
      detailedError.context
    );

    const {
      severity,
      message,
      stack,
      errorId,
      timestamp,
      ...contextWithoutDuplicates
    } = detailedError.context;
    baseReportError({
      severity: detailedError.context.severity,
      message: enrichedMessage, // Use enriched message instead of basic message
      stackTrace: detailedError.stack,
      errorId: detailedError.errorId,
      timestamp: detailedError.timestamp,
      originalMessage: detailedError.message,
      ExceptionContextMessage: exceptionContextMessage,
      ...contextWithoutDuplicates,
    });
  }

  return detailedError;
};

/**
 * Safely stringify objects, handling circular references
 */
const safeStringify = (obj: any, space?: number): string => {
  const seen = new Set();
  try {
    return JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        // Skip DOM elements and React components
        if (value.nodeType || value._reactInternalFiber || value.__reactFiber$qnubpgbrgei) {
          return '[DOM Element or React Component]';
        }
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    }, space);
  } catch (error) {
    return '[Unable to stringify: ' + String(error) + ']';
  }
};

/**
 * Log component errors from React Error Boundaries
 */
export const logComponentError = (
  error: Error,
  errorInfo: { componentStack: string },
  componentName: string,
  props?: any
) => {
  return logDetailedError(error, {
    severity: "ERROR",
    component: componentName,
    action: "component_error",
    componentStack: errorInfo.componentStack,
    props: props ? safeStringify(props, 2) : undefined,
  });
};

/**
 * Enhanced AppDynamics mobile reporting with detailed context
 */
const reportToAppDynamicsMobile = (detailedError: DetailedError) => {
  try {
    const AppdInstrumentation =
      require("@appdynamics/react-native-agent").Instrumentation;

    if (
      AppdInstrumentation &&
      typeof AppdInstrumentation.reportError === "function"
    ) {
      // First, ensure user data is set in AppDynamics session
      ensureUserDataInAppDynamics(detailedError.context);

      // Create enriched error message with detailed context
      const enrichedMessage = createEnrichedErrorMessage(detailedError);

      const errorObj = new Error(enrichedMessage);
      errorObj.stack = detailedError.stack;

      // Add context as properties (excluding duplicates)
      const contextToAdd = Object.fromEntries(
        Object.entries(detailedError.context).filter(
          ([key]) => !["severity", "message", "stack"].includes(key)
        )
      );

      Object.assign(errorObj, {
        errorId: detailedError.errorId,
        timestamp: detailedError.timestamp,
        originalMessage: detailedError.message,
        ...contextToAdd,
      });

      // Map severity
      let appdSeverity = "error";
      if (detailedError.context.severity === "WARN") appdSeverity = "warning";
      else if (detailedError.context.severity === "INFO") appdSeverity = "info";

      AppdInstrumentation.reportError(errorObj, appdSeverity);

      // Add breadcrumb for better tracking
      if (typeof AppdInstrumentation.leaveBreadcrumb === "function") {
        AppdInstrumentation.leaveBreadcrumb(
          `Error: ${detailedError.context.severity}`,
          {
            errorId: detailedError.errorId,
            message: detailedError.message,
            component: detailedError.context.component,
            action: detailedError.context.action,
            userId: detailedError.context.userId,
            locationName: detailedError.context.locationName,
            enrichedMessage: enrichedMessage.substring(0, 200), // First 200 chars for breadcrumb
          }
        );
      }
    }
  } catch (reportingError) {
    logger.error("Failed to report error to AppDynamics:", reportingError);
    const {
      severity,
      message,
      stack,
      errorId,
      timestamp,
      ...contextWithoutDuplicates
    } = detailedError.context;
    baseReportError({
      errorId: detailedError.errorId,
      timestamp: detailedError.timestamp,
      severity: detailedError.context.severity,
      message: detailedError.message,
      stackTrace: detailedError.stack,
      ...contextWithoutDuplicates,
    });
  }
};

/**
 * Create enriched error message with detailed context
 */
const createEnrichedErrorMessage = (detailedError: DetailedError): string => {
  const ctx = detailedError.context;

  let enrichedMessage = `[${ctx.severity}] ${detailedError.message}`;

  // Add component and screen context
  if (ctx.component) {
    enrichedMessage += ` | Component: ${ctx.component}`;
  }

  if (ctx.screen) {
    enrichedMessage += ` | Screen: ${ctx.screen}`;
  }

  // Add action context
  if (ctx.action) {
    enrichedMessage += ` | Action: ${ctx.action}`;
  }

  // Add API context if available
  if (ctx.apiEndpoint) {
    enrichedMessage += ` | API: ${ctx.httpMethod || "UNKNOWN"} ${
      ctx.apiEndpoint
    }`;
    if (ctx.statusCode) {
      enrichedMessage += ` (${ctx.statusCode})`;
    }
  }

  // Add user context
  if (ctx.userId) {
    enrichedMessage += ` | User: ${ctx.userId}`;
    if (ctx.userName && ctx.userName !== ctx.userId) {
      enrichedMessage += ` (${ctx.userName})`;
    }
  }

  if (ctx.locationName) {
    enrichedMessage += ` | Location: ${ctx.locationName}`;
  }

  // Add platform context
  if (ctx.platform) {
    enrichedMessage += ` | Platform: ${ctx.platform}`;
  }

  // Add timing if available
  if (ctx.responseTime) {
    enrichedMessage += ` | Response Time: ${ctx.responseTime}ms`;
  }

  // Add error ID for tracking
  enrichedMessage += ` | ErrorID: ${detailedError.errorId}`;

  return enrichedMessage;
};

/**
 * Get user display name from context or Redux state
 */
const getUserName = (context: EnhancedErrorContext): string => {
  // First try from context
  if (context.userName) {
    return context.userName;
  }

  // Fallback to Redux state
  try {
    const state = store.getState();
    const user = state?.auth?.user;
    return (
      user?.name ||
      user?.displayName ||
      user?.given_name ||
      user?.preferred_username ||
      context.userId ||
      "Unknown User"
    );
  } catch (e) {
    return context.userId || "Unknown User";
  }
};

/**
 * Ensure user data is properly set in AppDynamics session
 */
const ensureUserDataInAppDynamics = (context: EnhancedErrorContext) => {
  try {
    const AppdInstrumentation =
      require("@appdynamics/react-native-agent").Instrumentation;

    if (!AppdInstrumentation) return;

    // Create comprehensive exception context message
    const exceptionContextMessage = createExceptionContextMessage(context);

    // Set user data in AppDynamics session
    const userData = [
      ["userId", context.userId],
      ["email", context.userId], // User email/ID
      ["name", getUserName(context)], // User display name
      ["locationId", context.locationId],
      ["locationName", context.locationName],
      ["platform", context.platform],
      ["appVersion", context.appVersion],
      ["component", context.component],
      ["screen", context.screen],
      ["action", context.action],
      ["ExceptionContextMessage", exceptionContextMessage], // Key field for detailed context
      ["errorSeverity", context.severity],
      ["timestamp", context.timestamp],
      ["errorId", context.errorId],
    ];

    userData.forEach(([key, value]) => {
      if (value) {
        try {
          // Try multiple methods to set user data
          if (typeof AppdInstrumentation.setUserData === "function") {
            AppdInstrumentation.setUserData(key, value);
          } else if (typeof AppdInstrumentation.addUserData === "function") {
            AppdInstrumentation.addUserData(key, value);
          } else if (
            typeof AppdInstrumentation.setUserId === "function" &&
            key === "userId"
          ) {
            AppdInstrumentation.setUserId(value);
          }
        } catch (e) {
          logger.warn(`Failed to set AppDynamics user data ${key}:`, e);
        }
      }
    });

    logger.log(
      "✅ Enhanced user data with ExceptionContextMessage set in AppDynamics:",
      {
        userId: context.userId,
        userName: getUserName(context),
        email: context.userId,
        locationName: context.locationName,
        component: context.component,
        screen: context.screen,
        exceptionContext: exceptionContextMessage.substring(0, 100) + "...",
      }
    );
  } catch (e) {
    logger.warn("Failed to ensure user data in AppDynamics:", e);
  }
};

/**
 * Create detailed exception context message for AppDynamics user data
 */
const createExceptionContextMessage = (
  context: EnhancedErrorContext
): string => {
  const parts = [];

  // Error identification
  parts.push(`ERROR_ID: ${context.errorId}`);
  parts.push(`TIMESTAMP: ${context.timestamp}`);
  parts.push(`SEVERITY: ${context.severity}`);

  // Stack trace (truncated for readability)
  if (context.stack) {
    const truncatedStack = context.stack.substring(0, 500); // Limit stack trace length
    parts.push(`STACK_TRACE: ${truncatedStack.replace(/\n/g, " | ")}`);
  }

  // Application context
  if (context.component) parts.push(`COMPONENT: ${context.component}`);
  if (context.screen) parts.push(`SCREEN: ${context.screen}`);
  if (context.action) parts.push(`ACTION: ${context.action}`);
  if (context.platform) parts.push(`PLATFORM: ${context.platform}`);
  if (context.appVersion) parts.push(`APP_VERSION: ${context.appVersion}`);

  // User context
  if (context.userId) parts.push(`USER_ID: ${context.userId}`);
  if (context.userName) parts.push(`USER_NAME: ${context.userName}`);
  if (context.locationId) parts.push(`LOCATION_ID: ${context.locationId}`);
  if (context.locationName)
    parts.push(`LOCATION_NAME: ${context.locationName}`);

  // API context
  if (context.apiEndpoint) {
    parts.push(`API_ENDPOINT: ${context.apiEndpoint}`);
    if (context.httpMethod) parts.push(`HTTP_METHOD: ${context.httpMethod}`);
    if (context.statusCode) parts.push(`STATUS_CODE: ${context.statusCode}`);
    if (context.responseTime)
      parts.push(`RESPONSE_TIME: ${context.responseTime}ms`);
  }

  // Additional context
  if (context.capturedByInterceptor)
    parts.push(`INTERCEPTED: ${context.capturedByInterceptor}`);
  if (context.componentStack)
    parts.push(`COMPONENT_STACK: ${context.componentStack.substring(0, 200)}`);

  return parts.join(" | ");
};
